import { Lap } from '../lap/Lap'
import { LapMetadata } from '../lap/LapMetadata'
import { LapBounds } from '../lap/LapBounds'
import { RawTelemetryData } from '../telemetry/raw/RawTelemetryData'
import { ProcessedTelemetryData } from '../telemetry/processed/ProcessedTelemetryData'
import { GpsPoint } from '../telemetry/processed/GpsPoint'
import { TrackSessionMetadata, WeatherCondition } from './TrackSessionMetadata'
import { TrackSessionStatus } from './TrackSessionStatus'
import { segmentsIntersect } from '../../utils/geometryUtils'
import { calculateDistance } from '../../utils/distanceUtils'
import { computeBreakingPoints } from '../lap/lapAnalysis'
import { createSpatialBuckets } from '../../processors/spatial/spatialProcessing'
import { generateUuid, isEmptyGuid } from '../../utils/guidUtils'
import {
  Coordinate,
  Device,
  Guid,
  TrackSessionMetadataDto,
} from '../../api/apiTypes'
import { LatLng } from '../../types/LatLng'
import { calculateSamplingFrequencyHz } from '../../utils/timeUtils'

/**
 * Features available in a session (e.g., video, lean angle data)
 */
export interface SessionFeatures {
  hasVideo: boolean
  hasLeanAngle: boolean
}

/**
 * Track information compatible with both TrackDto and domain Track
 * This allows TrackSession to work with either format
 */
export interface TrackInfo {
  id: string
  name: string
  finishLine?: Coordinate[]
  countryCode?: string
  isUserSuggested?: boolean
}

export class TrackSession {
  // Mutable to allow updating after saving to backend
  id: string
  readonly name: string
  // Mutable to allow updating track after user suggestion
  track: TrackInfo
  readonly driverName: string
  readonly vehicleId?: string
  readonly vehicleName?: string
  readonly recordingDate: Date
  readonly weather?: WeatherCondition
  readonly notes?: string
  readonly sourceFileName?: string
  readonly device: Device

  private _laps: Map<number, Lap>
  private _status: TrackSessionStatus
  private _rawTelemetry: RawTelemetryData
  private _processedTelemetry: ProcessedTelemetryData
  private _isStoredInBackend: boolean
  private _sessionFeatures: SessionFeatures
  private _lapsIdentifiers?: Record<number, Guid>
  private _metadata?: TrackSessionMetadataDto
  private _sharedLapIds?: Set<string>

  constructor(
    metadata: TrackSessionMetadata,
    track: TrackInfo,
    processedTelemetry: ProcessedTelemetryData,
    rawTelemetry: RawTelemetryData,
    device: Device,
    sessionFeatures: SessionFeatures = { hasVideo: false, hasLeanAngle: false },
    status: TrackSessionStatus = TrackSessionStatus.Processing
  ) {
    this.id = metadata.sessionId
    this.name = metadata.sessionName
    this.track = track
    this.driverName = metadata.driverName
    this.vehicleId = metadata.vehicleId
    this.vehicleName = metadata.vehicleName
    this.recordingDate = metadata.recordingDate
    this.sourceFileName = metadata.sourceFileName
    this.weather = metadata.weather
    this.notes = metadata.notes
    this.device = device
    this._laps = new Map()
    this._processedTelemetry = processedTelemetry
    this._rawTelemetry = rawTelemetry
    this._status = status
    this._isStoredInBackend = false
    this._sessionFeatures = sessionFeatures
  }

  get status(): TrackSessionStatus {
    return this._status
  }

  get laps(): Lap[] {
    return Array.from(this._laps.values()).sort(
      (a, b) => a.lapNumber - b.lapNumber
    )
  }

  /**
   * Replace the lap array entirely
   * @deprecated Prefer using addLap/removeLap methods. This setter exists for backward compatibility.
   */
  set laps(value: Lap[]) {
    this._laps.clear()
    value.forEach((lap) => this._laps.set(lap.lapNumber, lap))
  }

  get lapCount(): number {
    return this._laps.size
  }

  get isStoredInBackend(): boolean {
    return this._isStoredInBackend
  }

  /** @deprecated Use isStoredInBackend instead */
  get isStored(): boolean {
    return this._isStoredInBackend
  }

  /** @deprecated Use markAsStoredInBackend() instead */
  set isStored(value: boolean) {
    this._isStoredInBackend = value
    if (value && this._status === TrackSessionStatus.Processing) {
      this._status = TrackSessionStatus.Completed
    }
  }

  get rawTelemetry(): RawTelemetryData {
    return this._rawTelemetry
  }

  get processedTelemetry(): ProcessedTelemetryData {
    return this._processedTelemetry
  }

  /** Alias for processedTelemetry to match web app naming */
  get telemetry(): ProcessedTelemetryData {
    return this._processedTelemetry
  }

  get sessionFeatures(): SessionFeatures {
    return this._sessionFeatures
  }

  get lapsIdentifiers(): Record<number, Guid> | undefined {
    return this._lapsIdentifiers
  }

  get metaData(): TrackSessionMetadataDto | undefined {
    return this._metadata
  }

  get sharedLapIds(): Set<string> | undefined {
    return this._sharedLapIds
  }

  /** Alias for name to support legacy web app code */
  get fileName(): string {
    return this.name
  }

  set lapsIdentifiers(value: Record<number, Guid> | undefined) {
    this._lapsIdentifiers = value
  }

  set metaData(value: TrackSessionMetadataDto | undefined) {
    this._metadata = value
  }

  set sharedLapIds(value: Set<string> | undefined) {
    this._sharedLapIds = value
  }

  addLap(lap: Lap): void {
    if (lap.sessionId !== this.id) {
      throw new Error(`Lap ${lap.id} does not belong to this track session`)
    }
    this._laps.set(lap.lapNumber, lap)
  }

  getLap(lapNumber: number): Lap | undefined {
    return this._laps.get(lapNumber)
  }

  getLapById(lapId: string): Lap | undefined {
    return Array.from(this._laps.values()).find((lap) => lap.id === lapId)
  }

  removeLap(lapNumber: number): void {
    this._laps.delete(lapNumber)
  }

  setLaps(lapIds: Record<number, Guid>): void {
    // Since lap.id is readonly, we need to recreate laps with new IDs
    const currentLaps = this.laps
    this._laps.clear()

    currentLaps.forEach((lap, index) => {
      const newLapId = lapIds[index]
      if (!newLapId) {
        this._laps.set(lap.lapNumber, lap) // Keep original if no new ID provided
        return
      }

      // Recreate lap with new ID
      const metadata: LapMetadata = {
        lapId: newLapId,
        lapNumber: lap.lapNumber,
        driverName: lap.driverName,
        trackSessionId: lap.sessionId,
        trackId: lap.trackId,
        vehicleId: lap.vehicleId,
        vehicleName: lap.vehicleName,
      }

      const newLap = new Lap(
        metadata,
        lap.bounds,
        lap.processedTelemetryData,
        lap.rawTelemetry,
        {
          device: lap.device,
          sessionName: lap.sessionName,
          gyro: lap.gyro,
          accl: lap.accl,
          weather: lap.weather,
        }
      )

      // Preserve UI state
      newLap.isSelected = lap.isSelected
      newLap.isShared = lap.isShared

      // Preserve analysis results
      newLap.breakingPoints = lap.breakingPoints
      newLap.slowestPoints = lap.slowestPoints
      newLap.topSpeed = lap.topSpeed

      this._laps.set(newLap.lapNumber, newLap)
    })
  }

  setStatus(status: TrackSessionStatus): void {
    this._status = status
  }

  setRawTelemetry(telemetry: RawTelemetryData): void {
    this._rawTelemetry = telemetry
  }

  setProcessedTelemetry(telemetry: ProcessedTelemetryData): void {
    this._processedTelemetry = telemetry
  }

  /** Alias for setProcessedTelemetry to match web app naming */
  setTelemetry(telemetry: ProcessedTelemetryData): void {
    this._processedTelemetry = telemetry
  }

  markAsStoredInBackend(): void {
    this._isStoredInBackend = true
    if (this._status === TrackSessionStatus.Processing) {
      this._status = TrackSessionStatus.Completed
    }
  }

  /**
   * Check if track is unknown based on name
   */
  private isTrackUnknown(): boolean {
    return this.track.name === 'Unknown'
  }

  hasKnownTrack(): boolean {
    return !this.isTrackUnknown() && !isEmptyGuid(this.track.id)
  }

  getBestLap(): Lap | undefined {
    if (this.lapCount === 0) return undefined
    return this.laps.reduce((best, current) =>
      current.lapTime < best.lapTime ? current : best
    )
  }

  getAverageLapTime(): number | undefined {
    if (this.lapCount === 0) return undefined
    const totalTime = this.laps.reduce((sum, lap) => sum + lap.lapTime, 0)
    return totalTime / this.lapCount
  }

  getTotalDistance(): number {
    if (this.lapCount === 0) return 0
    return this.laps.reduce((sum, lap) => sum + lap.getLapDistance(), 0)
  }

  getTotalDuration(): number {
    if (this.lapCount === 0) return 0
    const firstLap = this.laps[0]
    const lastLap = this.laps[this.lapCount - 1]
    return (
      lastLap.bounds.finishTime.getTime() - firstLap.bounds.startTime.getTime()
    )
  }

  hasLaps(): boolean {
    return this.lapCount > 0
  }

  isValid(): boolean {
    return this.track.id !== undefined && this.driverName !== undefined
  }

  /**
   * Apply start/finish line to detect laps in the session telemetry
   * @param sessionName Session name/number for lap grouping
   * @param finishLine Two points defining the start/finish line
   */
  applyStartFinishForSession(sessionName: number, finishLine: LatLng[]): void {
    let internalFinishLine = finishLine

    // Use track's finish line if available (supports both 1-point and 2-point)
    if (
      !this.isTrackUnknown() &&
      this.track.finishLine &&
      this.track.finishLine.length >= 1
    ) {
      internalFinishLine = this.track.finishLine.map((c) => ({
        latitude: c.latitude,
        longitude: c.longitude,
      }))
    }

    const gpsPoints = this._processedTelemetry.gpsPoints
    if (
      !gpsPoints ||
      gpsPoints.length === 0 ||
      internalFinishLine.length === 0
    ) {
      throw new Error('Invalid telemetry or finish line')
    }

    // Find crossings depending on finish line type
    const intersections: number[] =
      internalFinishLine.length >= 2
        ? this.findLineIntersections(gpsPoints, internalFinishLine)
        : this.findNearestApproachCrossings(gpsPoints, internalFinishLine[0])

    if (intersections.length === 0) {
      return // No laps detected
    }

    // Store existing selections and shared state
    const sharedLaps = this.laps.filter((lap) => lap.isShared)
    const existingSelections = new Map(
      this.laps.map((lap) => [lap.lapNumber, lap.isSelected])
    )

    // Clear existing laps
    this._laps.clear()

    // Create laps from intersections
    for (let i = 0; i < intersections.length - 1; i++) {
      const startIndex = intersections[i]
      const endIndex = intersections[i + 1]
      const lapGpsPoints = gpsPoints.slice(startIndex, endIndex)

      if (lapGpsPoints.length === 0) continue

      const lapStartTime = lapGpsPoints[0].timestamp
      const lapFinishTime = lapGpsPoints[lapGpsPoints.length - 1].timestamp

      let lapId = generateUuid()
      if (this._lapsIdentifiers?.[i]) {
        lapId = this._lapsIdentifiers[i]
      }

      // Extract processed telemetry for this lap
      const lapProcessedTelemetry = this.extractLapTelemetry(
        this._processedTelemetry,
        lapStartTime,
        lapFinishTime
      )

      const metadata: LapMetadata = {
        lapId,
        lapNumber: i,
        driverName: this.driverName || 'Unknown',
        trackSessionId: this.id,
        trackId: this.track.id,
      }

      const bounds: LapBounds = {
        startTime: lapStartTime,
        finishTime: lapFinishTime,
        startDistanceMeters: 0, // Lap-relative
        finishDistanceMeters:
          lapGpsPoints[lapGpsPoints.length - 1].distanceMeters -
          lapGpsPoints[0].distanceMeters,
      }

      // Create spatial buckets for this lap
      const spatialBuckets = createSpatialBuckets(lapProcessedTelemetry)

      // Extract lap-specific raw telemetry
      const lapRawTelemetry = this.extractLapRawTelemetry(
        lapStartTime,
        lapFinishTime
      )

      // Create the lap
      const lap = new Lap(
        metadata,
        bounds,
        lapProcessedTelemetry,
        lapRawTelemetry,
        {
          device: this.device,
          sessionName: sessionName.toString(),
          gyro: this._rawTelemetry.gyroscope?.filter(
            (x) => lapStartTime < x.timestamp && x.timestamp < lapFinishTime
          ),
          accl: this._rawTelemetry.accelerometer?.filter(
            (x) => lapStartTime < x.timestamp && x.timestamp < lapFinishTime
          ),
          spatialBuckets,
        }
      )

      // Compute breaking and slowest points
      const result = computeBreakingPoints(lap)
      lap.breakingPoints = result.breakingPoints
      lap.slowestPoints = result.slowestPoints
      lap.topSpeed = result.topSpeed

      this._laps.set(i, lap)
    }

    if (this._laps.size === 0) {
      return // No complete laps found
    }

    // Restore shared and selection state
    for (const lap of this.laps) {
      const isInSharedSet = this._sharedLapIds?.has(lap.id)
      const wasSharedBefore = sharedLaps.some(
        (l) => l.lapNumber === lap.lapNumber
      )

      if (wasSharedBefore || isInSharedSet) {
        lap.isShared = true
      }

      if (existingSelections.has(lap.lapNumber)) {
        lap.isSelected = existingSelections.get(lap.lapNumber) ?? false
      }
    }
  }

  /**
   * Extract processed telemetry for a specific lap time range
   */
  private extractLapTelemetry(
    sessionTelemetry: ProcessedTelemetryData,
    lapStartTime: Date,
    lapFinishTime: Date
  ): ProcessedTelemetryData {
    const startMs = lapStartTime.getTime()
    const finishMs = lapFinishTime.getTime()

    // Find index range
    let startIndex = 0
    let endIndex = sessionTelemetry.gpsPoints.length - 1

    for (let i = 0; i < sessionTelemetry.gpsPoints.length; i++) {
      if (sessionTelemetry.gpsPoints[i].timestamp.getTime() >= startMs) {
        startIndex = i
        break
      }
    }

    for (let i = startIndex; i < sessionTelemetry.gpsPoints.length; i++) {
      if (sessionTelemetry.gpsPoints[i].timestamp.getTime() > finishMs) {
        endIndex = i - 1
        break
      }
    }

    const gpsPoints = sessionTelemetry.gpsPoints.slice(startIndex, endIndex + 1)
    if (gpsPoints.length === 0) {
      throw new Error('No GPS points found for lap time range')
    }

    // Adjust to lap-relative timing and distance
    const startElapsedMs = gpsPoints[0].elapsedMs
    const startDistance = gpsPoints[0].distanceMeters
    const adjustedGpsPoints = gpsPoints.map((p) => ({
      ...p,
      elapsedMs: p.elapsedMs - startElapsedMs,
      distanceMeters: p.distanceMeters - startDistance,
    }))

    // Extract and adjust derived data using the same index range as GPS
    // The pipeline's TelemetrySynchronizationStage ensures all arrays have the same length
    const leanAngle = sessionTelemetry.leanAngle
      .slice(startIndex, endIndex + 1)
      .map((p) => ({
        ...p,
        elapsedMs: p.elapsedMs - startElapsedMs,
        distanceMeters: p.distanceMeters - startDistance,
      }))

    const acceleration = sessionTelemetry.acceleration
      .slice(startIndex, endIndex + 1)
      .map((p) => ({
        ...p,
        elapsedMs: p.elapsedMs - startElapsedMs,
        distanceMeters: p.distanceMeters - startDistance,
      }))

    const gForce = sessionTelemetry.gForce
      .slice(startIndex, endIndex + 1)
      .map((p) => ({
        ...p,
        elapsedMs: p.elapsedMs - startElapsedMs,
        distanceMeters: p.distanceMeters - startDistance,
      }))

    // Calculate lap statistics
    const speeds = adjustedGpsPoints.map((p) => p.speedKmh)
    const totalDurationMs = finishMs - startMs

    const statistics = {
      maxSpeedKmh: Math.round(Math.max(...speeds, 0) * 100) / 100,
      avgSpeedKmh:
        Math.round(
          (speeds.reduce((sum, s) => sum + s, 0) / speeds.length || 0) * 100
        ) / 100,
      totalDistanceMeters: Math.round(
        adjustedGpsPoints[adjustedGpsPoints.length - 1]?.distanceMeters ?? 0
      ),
      totalDurationMs,
      dataPointCount: adjustedGpsPoints.length,
      samplingFrequencyHz: calculateSamplingFrequencyHz(adjustedGpsPoints),
      maxLeanAngle:
        leanAngle.length > 0
          ? Math.max(...leanAngle.map((p) => Math.abs(p.leanAngleDegrees)))
          : 0,
      maxLongitudinalG:
        gForce.length > 0
          ? Math.max(...gForce.map((p) => Math.abs(p.longitudinalG)))
          : 0,
      maxLateralG:
        gForce.length > 0
          ? Math.max(...gForce.map((p) => Math.abs(p.lateralG)))
          : 0,
      maxCombinedG:
        gForce.length > 0 ? Math.max(...gForce.map((p) => p.combinedG)) : 0,
      maxAccelerationMs2:
        acceleration.length > 0
          ? Math.max(
              ...acceleration
                .filter((p) => p.longitudinalMs2 > 0)
                .map((p) => p.longitudinalMs2),
              0
            )
          : 0,
      maxBrakingMs2:
        acceleration.length > 0
          ? Math.abs(
              Math.min(
                ...acceleration
                  .filter((p) => p.longitudinalMs2 < 0)
                  .map((p) => p.longitudinalMs2),
                0
              )
            )
          : 0,
    }

    return {
      gpsPoints: adjustedGpsPoints,
      leanAngle,
      acceleration,
      gForce,
      statistics,
    }
  }

  /**
   * Extract raw telemetry for a specific lap time range
   */
  private extractLapRawTelemetry(
    lapStartTime: Date,
    lapFinishTime: Date
  ): RawTelemetryData {
    const lapStartTimeMs = lapStartTime.getTime()
    const lapFinishTimeMs = lapFinishTime.getTime()

    const lapGpsReadings = this._rawTelemetry.gps.filter(
      (reading) =>
        reading.timestamp.getTime() >= lapStartTimeMs &&
        reading.timestamp.getTime() <= lapFinishTimeMs
    )

    // Adjust distance to be lap-relative
    const startDistance = lapGpsReadings[0]?.distanceMeters ?? 0
    const adjustedGpsReadings = lapGpsReadings.map((reading) => ({
      ...reading,
      distanceMeters: reading.distanceMeters
        ? reading.distanceMeters - startDistance
        : 0,
    }))

    return {
      gps: adjustedGpsReadings,
      gyroscope: this._rawTelemetry.gyroscope?.filter(
        (reading) =>
          reading.timestamp.getTime() >= lapStartTimeMs &&
          reading.timestamp.getTime() <= lapFinishTimeMs
      ),
      accelerometer: this._rawTelemetry.accelerometer?.filter(
        (reading) =>
          reading.timestamp.getTime() >= lapStartTimeMs &&
          reading.timestamp.getTime() <= lapFinishTimeMs
      ),
      recordingStartTime: lapStartTime,
      device: this._rawTelemetry.device,
    }
  }

  /**
   * Find intersections with a 2-point finish line segment.
   */
  private findLineIntersections(
    gpsPoints: GpsPoint[],
    finishLine: LatLng[]
  ): number[] {
    const intersections: number[] = []
    for (let i = 0; i < gpsPoints.length - 1; i++) {
      const intersects = segmentsIntersect(
        gpsPoints[i].latitude,
        gpsPoints[i].longitude,
        gpsPoints[i + 1].latitude,
        gpsPoints[i + 1].longitude,
        finishLine[0].latitude,
        finishLine[0].longitude,
        finishLine[1].latitude,
        finishLine[1].longitude
      )
      if (intersects) {
        intersections.push(i)
      }
    }
    return intersections
  }

  /**
   * Find lap crossings using nearest-approach to a single reference point.
   * Scans GPS points for "windows" where distance to the reference drops
   * below a threshold, and picks the closest point in each window.
   */
  private findNearestApproachCrossings(
    gpsPoints: GpsPoint[],
    refPoint: LatLng,
    thresholdMeters: number = 50,
    minLapTimeMs: number = 10000
  ): number[] {
    const crossings: number[] = []
    let inWindow = false
    let windowMinDist = Infinity
    let windowMinIndex = -1
    let lastCrossingTime = -Infinity

    for (let i = 0; i < gpsPoints.length; i++) {
      const dist = calculateDistance(
        gpsPoints[i].latitude,
        gpsPoints[i].longitude,
        refPoint.latitude,
        refPoint.longitude
      )

      if (dist < thresholdMeters) {
        if (!inWindow) {
          inWindow = true
          windowMinDist = dist
          windowMinIndex = i
        } else if (dist < windowMinDist) {
          windowMinDist = dist
          windowMinIndex = i
        }
      } else if (inWindow) {
        inWindow = false
        const crossingTime = gpsPoints[windowMinIndex].timestamp.getTime()
        if (crossingTime - lastCrossingTime >= minLapTimeMs) {
          crossings.push(windowMinIndex)
          lastCrossingTime = crossingTime
        }
        windowMinDist = Infinity
        windowMinIndex = -1
      }
    }

    // Handle track ending inside a window
    if (inWindow && windowMinIndex >= 0) {
      const crossingTime = gpsPoints[windowMinIndex].timestamp.getTime()
      if (crossingTime - lastCrossingTime >= minLapTimeMs) {
        crossings.push(windowMinIndex)
      }
    }

    return crossings
  }
}
